/** @module csi/reflectionHints */
define([
	"dojo/_base/lang",
	"csi",
	"csi/_base/underscore",
	"csi/reflection/reflectionConstants",
	"csi/xml",
], function(lang, csi, _, {TYPE_DATA_SETUP, TYPE_HOME, TYPE_SITE, TYPE_TASKS}, xml) {

	const ACCESS = "Access";
	const LINK = "Link";
	const SORT = "Sort";
	const WATCH = "Watch";
	const FREEZE_EXCLUSION_LIST = ["SupplierRequest", "CustomerPurchaseOrder", "SalesOrder", "MarketingCollection", "PurchasedOrder", "Project", "EngineeringChange"];
	const IMAGE_CONTAINERS_ALLOW_ATTRIBUTES = ["LabeledImage"];
	const IMAGE_CONTAINERS_ALLOW_EDIT = ["User"];

	const TYPES_WITH_DUPLICATE_PARENT = {
		Colorway: "Style",
		Category1: "ParentSeason",
		Category2: "Category1",
		Collection: "Category2",
		SizeChartReview: "Style",
		SKU: "Style",
		Style: "Collection"
	};

	const ISSUE_LOG_ATTRS = ["IssueCategory1", "IssueCategory2", "IssueCollection", "IssueProduct", "IssueSeason"];

	/**
	 * Attributes configured below should be hidden when showing attributes from the whole domain together;
	 * this should only occur with attributes on secondary classes, as if on the domain class, the
	 * domain class could override the attribute and mark it private. That cannot always be the case with
	 * secondary classes, such as IssueContainer/Log.
	 */
	const PRIVATE_SECONDARY_ATTRIBUTES = {
		Category1: ISSUE_LOG_ATTRS,
		Category2: ISSUE_LOG_ATTRS,
		Collection: ISSUE_LOG_ATTRS,
		Material: ISSUE_LOG_ATTRS,
		Season: ISSUE_LOG_ATTRS,
		Style: ISSUE_LOG_ATTRS
	};

	/**
	 * Mapping of template types to function returning true if given node is a template
	 */
	const EMPTY_PARENT = {
		[xml.ATTR_PARENT]: csi.EMPTY_URL
	};

	const URL_SITELIBSUPPLIERITEM = "centric://REFLECTION/INSTANCE/SiteLibSupplierItem/SiteLibSupplierItem";

	const TEMPLATE_TYPE_CONDITIONS = {
		ApparelBOM: EMPTY_PARENT,
		CanvasTemplate: {},
		DataPackageTemplate: {},
		SustainabilityProfile: EMPTY_PARENT,
		FactoryReviewTemplate: {},
		FinalInspectionTemplate: {},
		FoodBOM: EMPTY_PARENT,
		FoodLabelDataSheet: EMPTY_PARENT,
		FoodSupplierItem: {
			[xml.ATTR_PARENT]: URL_SITELIBSUPPLIERITEM
		},
		OperationsReviewTemplate: {},
		Routing: {
			[xml.ATTR_PARENT]: URL_SITELIBSUPPLIERITEM
		},
		SizeChart: EMPTY_PARENT,
		SpecificationDataSheet: EMPTY_PARENT,
		Style: {
			IsTemplate: true,
			IsInspiration: false
		},
		SupplierItem: {
			[xml.ATTR_PARENT]: URL_SITELIBSUPPLIERITEM
		},
		SupplierRequestTemplate: {},
		TestRun: {
			[xml.ATTR_PARENT]: "centric://QUALITY/QualityAdmin"
		},
		Wbs: EMPTY_PARENT,
	};

	const TEMPLATE_TYPES_WITH_STATE = {
		FactoryReviewTemplate: 1,
		OperationsReviewTemplate: 1,
		TestRun: 1,
	};

	const REVISION_TYPES_WITH_APPROVED_LATEST = {
		ContractualDocumentRevision: 1,
		SubRoutingRevision: 1,
		TestRunRevision: 1,
		TestSpecRevision: 1,
	};

	const _locationHistoryCallbacks = {};
	const _locationLayoutCallbacks = {};

	/**
	 * Paths added to the Comments view for each type
	 * @type {Record<string,string[]>
	 */
	const _commentPaths = {};

	/**
	 * @type {Record<string,string[]>
	 */
	const specialPaths = {};

	/**
	 * Add path to DocAndCommentOwner from given type to be displayed in Comments view
	 * @param {string} type
	 * @param {string} path
	 */
	function addCommentPath(type, path) {
		// NOTE: for now assuming all configured paths lead objects that are DocAndCommentOwners (not CommentContainer - just a few of them)
		csi.objectArrayAddValue(_commentPaths, type, path);
	}

	/**
	 * Return extra paths comments to use in Comments view of given type
	 * @param {string} type
	 * @return {string[]}
	 */
	function getCommentPaths(type) {
		let paths = _commentPaths[type] || [];

		return paths;
	}

	/**
	 * Return true if there are comment paths registered for the given type
	 * @param {string} type
	 * @return {boolean}
	 */
	function hasCommentPaths(type) {
		return !!getCommentPaths(type).length;
	}

	/**
	 * Remove comment path from given type
	 * @param {string} type
	 * @param {string} path
	 */
	function removeCommentPath(type, path) {
		const paths = _commentPaths[type];
		if (paths) {
			_commentPaths[type] = _.without(paths, path);
		}
	}

	/**
	 * Return query containing conditions to query for templates of the given type
	 * @param {string} type
	 * @param {Query} q
	 */
	function addQueryConditionsForTemplate(type, q) {
		const conditions = TEMPLATE_TYPE_CONDITIONS[type];

		if (conditions) {
			for (const [key, value] of Object.entries(conditions)) {
				if (key === xml.ATTR_PARENT) {
					q.RefAttr(key, xml.OP_EQUALS, value);
				} else {
					q.Attr(key, xml.OP_EQUALS, value);
				}
			}
		}
	}

	/**
	 * Determine if given master node supports state, which occurs when not a template (except for TestRun)
	 * @param {C8Node} masterNode
	 * @return {boolean}
	 */
	function isRevisableNodeWithState(masterNode) {
		return TEMPLATE_TYPES_WITH_STATE[masterNode.$Type] || !isTemplateNode(masterNode);
	}

	/**
	 * Determine if given revision node supports approved / latest state
	 * @param {C8Node} revisionNode
	 * @return {boolean}
	 */
	function isRevisableNodeWithApprovedLatest(revisionNode) {
		return REVISION_TYPES_WITH_APPROVED_LATEST[revisionNode.$Type];
	}

	/**
	 * Determine if given node is a template
	 * @param {C8Node} node
	 * @return {boolean}
	 */
	function isTemplateNode(node) {
		const type = node && node.$Type;
		const conditions = TEMPLATE_TYPE_CONDITIONS[type];

		if (!conditions) {
			return false;
		}

		for (const [key, value] of Object.entries(conditions)) {
			if (node[key] !== value) {
				return false;
			}
		}

		return true;
	}

	/**
	 * Determine if given type is a template
	 * @param {string} type
	 * @return {boolean}
	 */
	function isTemplateType(type) {
		return !!TEMPLATE_TYPE_CONDITIONS[type];
	}

	// Object: lookup map for classes:
	// 		Access: Object
	// 			maps relations to a function that determines if editing should occur when traversed -
	// 			the general rule for this is to allow editing for aggregation/association relationships that
	// 			point to nodes that are elsewhere contained by a composition relationship
	//
	// 		Browse: Function(/*Node*/node, /*Cache*/cache)
	// 			function that adds special logic to determine if user can navigate to a node (if not return null),
	//			and if so, to what node (currently only used in Material Schedule)
	//
	// 		Child/Parent: Object
	// 			maps destination class for path operators along given relation - used to help resolve ambiguous paths
	//
	// 		Special: String
	// 			indicates path to type for special items where Form Definition does not apply
	//
	// 		View: ViewRef
	//			FIX: Deprecate for 5.0 - this should no longer be needed once we support better custom view filtering
	// 			reference of view to use when filtering by given type in custom views
	//
	// 		Watch: Function(/*Controller*/ac, /*ChangeNode*/cn, /*Node*/node, /*String*/idAttr, /*Boolean*/bRestore, /*Boolean*/bDisable)
	// 			function to call when attribute value is changed or its expression is restored or disabled
	//
	// 	{
	//		"Class1": {
	//			Access:{
	//				"RelationId": Function(node)
	//			},
	//			Child:{
	//				"RelationId":"Class2"
	//			}
	//		},
	//		"Class2": {
	//			Parent: {
	//				"RelationId":"Class1"
	//			}
	//		}
	//	}
	const _hints = {
		"ActionItemStepResponse": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"ActionItemStep": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"ApparelBOMRevision": {
			Access: {
				"Review": Boolean
			},
		},
		"Artwork": {
			Child: {
				"__Parent__": "BaseProduct"
			}
		},
		"AvatarLibrary": {
			Access: {
				"Avatars": Boolean
			}
		},
		"BaseActivity": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"BasePurchaseOrder": {
			Child: {
				"Orders": "BaseOrder"
			}
		},
		"BaseProduct": {
			Access: {
				"SharedSpecSections": Boolean,
				"SharedSectionItemMap": Boolean,
				"SharedSpecItemDefinitionMap": Boolean
			}
		},
		"BOMLineItem": {
			Child: {
				"__Parent__": "BaseBOMRevision",
				"Actual": "BaseProduct",
				"Copy": "BaseProduct",
				"Original": "BaseProduct"
			}
		},
		"BPProductPlan": {
			Access: {
				"Children": Boolean
			}
		},
		"Calendar": {
			Access: {
				"AllActivities": Boolean,
				"Children": Boolean,
				"Master": Boolean
			}
		},
		"CamundaTask": {
			Access: {
				"__Parent__": Boolean,
				"C8Instance": Boolean
			}
		},
		"Category1Attributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"Category2Attributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"CollectionAttributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"CollectionColor": {
			Access: {
				"__Parent__": Boolean
			},
			Child: {
				"PhasedColor": "ColorMaterial"
			}
		},
		"ColorColorwayContext": {
			Access: {
				"Actual": function(node) {return node.Copy != csi.EMPTY_URL;}
			}
		},
		"ColorInPalette": {
			Access: {
				"Actual": function(node) {return node.Copy != csi.EMPTY_URL;}
			}
		},
		"ColorSpecification": {
			Parent: {
				"Actual": "ColorColorwayContext"
			}
		},
		"ColorwayAttributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"CompanyInfo": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"Configuration": {
			Access: {
				"WorkflowApprovableSetupSettingses": Boolean
			}
		},
		"ContractualDocumentGroup": {
			Access: {
				"ContractualDocuments": Boolean
			}
		},
		"CostRoutingGroup": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"CostScenario": {
			Access: {
				"__Parent__": Boolean
			},
			Child: {
				"__Parent__": "SupplierItemRevision"
			}
		},
		"CreateFormDefinition": {
			Access: {
				"ConfigurableAttributes": Boolean
			}
		},
		"Crew": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"CustomerPurchaseOrder": {
			Child: {
				"Orders": "CustomerOrder"
			}
		},
		"DataSheet": {
			Access: {
				"DataSheetSamples": Boolean,
				"LinkedSourceDataSheets": Boolean,
			},
		},
		"Document": {
			Access: {
				"AllVariants": Boolean
			},
			Child: {
				"__Parent__": "DocumentContainer"
			}
		},
		"DocumentRevision": {
			Access: {
				"Variants": Boolean
			}
		},
		"DSLineItem": {
			Access: {
				"Actual": function(node) {return node.Copy != csi.EMPTY_URL;}
			}
		},
		"Factory": {
			Access: {
				"FactorySupplierFactories": Boolean,
				"Suppliers": Boolean
			},
		},
		"Food": {
			Access: {
				"CompetitiveFoods": Boolean
			},
		},
		"FoodBOMLineItem": {
			Child: {
				"Actual": "BaseProduct",
				"BOMLineQuote": "BaseSupplierItem",
				"BOMLineSKU": "BaseRealizedProduct",
				"Copy": "BaseProduct",
				"Original": "BaseProduct"
			}
		},
		"FoodLabelDataSheet": {
			Child: {
				"__Parent__": "BaseProduct"
			}
		},
		"FoodSKU": {
			Access: {
				"SKUArtworks": Boolean,
				"SKUFoodBOM": Boolean,
				"SKUFoodLabel": Boolean,
				"SKURecipe": Boolean,
				"SKUSpecs": Boolean,
				"SKUTestRuns": Boolean
			},
			Child: {
				"__Parent__": "Food"
			}
		},
		"FoodSource": {
			Child: {
				"__Parent__": "FormulatedMaterialSourcing",
			}
		},
		"FoodSupplierItem": {
			Access: {
				"__Parent__": Boolean
			},
			Child: {
				"__Parent__": "FoodSource"
			}
		},
		"ImageContainer": {
			Access: {
				"Images": Boolean
			}
		},
		"LibCareSymbol": {
			Access: {
				"Elements": Boolean
			},
		},
		"LibColorSpecification": {
			Access: {
				"Elements": Boolean
			},
		},
		"LibMaterial": {
			Access: {
				"Elements": Boolean
			},
		},
		"LibPrintDesign": {
			Access: {
				"Elements": Boolean
			},
		},
		"LibSizeLabel": {
			Access: {
				"Elements": Boolean
			},
		},
		"MarketingLook": {
			Access: {
				"SalesLooks": Boolean
			}
		},
		"MaterialAttributes": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"MaterialBOMRevision": {
			Access: {
				"Review": Boolean
			},
		},
		"MaterialComposition": {
			Child: {
				"__Parent__": "MaterialAttributes"
			}
		},
		"MaterialDataSheet": {
			Access: {
				"DataSheets": Boolean
			},
		},
		"MaterialPlanPlacementColorException": {
			Child: {
				"ExceptionBOMs": "ApparelBOM"
			}
		},
		"MaterialPlanPlacementColorway": {
			Access: {
				"MPCMaterial": Boolean,
				"MPCColor": Boolean
			}
		},
		"MaterialPlanPlacementException": {
			Child: {
				"ExceptionBOMs": "ApparelBOM"
			}
		},
		"MaterialSample": {
			Access: {
				"__Parent__": function(node) {return node.HiddenProduct;}
			},
		},
		"MaterialSchedule": {
			Access: {
				"BOM": Boolean
			},
			Child: {
				"__Parent__": "StructureItem"
			}
		},
		"MaterialScheduleRevision": {
			Access: {
				"BOM": Boolean
			}
		},
		"MaterialSourcing": {
			Access: {
				"__Parent__": Boolean,
				"DefaultQuote": Boolean
			},
		},
		"MaterialSpecification": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"MerchCollectionVersion": {
			Access: {
				"__Parent__": Boolean,
				"Children": Boolean
			},
		},
		"MerchFolder": {
			Child: {
				"__Parent__": "MerchFolder",
				"Children": "MerchLevel",
			}
		},
		"MerchFolderVersion": {
			Access: {
				"__Parent__": Boolean,
				"Children": Boolean
			},
		},
		"MerchOptionVersion": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"MerchProductVersion": {
			Access: {
				"__Parent__": Boolean,
				"Children": Boolean
			},
		},
		"MerchProductVersionCopyTemplate": {
			Child: {
				"Subtype": "MerchProductSubtype"
			}
		},
		"MerchSeason": {
			Access: {
				"__Parent__": Boolean,
				"Children": Boolean
			},
		},
		"MerchSecondaryVersion": {
			Child: {
				"__Parent__": "MerchSecondary",
			}
		},
		"MTSection": {
			Access: {
				"MTSectionDocuments": Boolean
			}
		},
		"OperationsReview": {
			Access: {
				"ReviewIssues": Boolean
			}
		},
		"Order": {
			Access: {
				"Shipments": Boolean
			},
		},
		"PartMaterial": {
			Access: {
				"PartMaterialColorsFiltered": Boolean
			},
			Child: {
				"Actual": "Product",
				"Original": "Product",
				"__Parent__": "ProductBOMRevision"
			}
		},
		"PrintDesignRevision": {
			Access: {
				"RevisionColors": Boolean
			}
		},
		"PrintSpecification": {
			Access: {
				"ColorSpecifications": Boolean
			}
		},
		"Product": {
			Access: {
				"PurchasedOrderProducts": Boolean,
				"SharedSectionItemMap": Boolean,
				"SharedSpecItemDefinitionMap": Boolean
			}
		},
		"ProductBOM": {
			Child: {
				"__Parent__": "Product"
			}
		},
		"ProductBOMRevision": {
			Child: {
				"Items": "PartMaterial"
			}
		},
		"ProductColor": {
			Access: {
				"ColorwayColorSources": Boolean,
				"PurchasedOrderColors": Boolean,
				"Samples": Boolean
			}
		},
		"ProductColorSpec": {
			Child: {
				"__Parent__": "PrintDesign"
			}
		},
		"ProductSample": {
			Access: {
				"__Parent__": function(node) {return node.HiddenProduct;},
				"ProductColor": function(node) {return node.HiddenProduct;},
				"ProductSize": function(node) {return node.HiddenProduct;},
				"SampleDataSheets": Boolean
			}
		},
		"ProductSource": {
			Access: {
				"__Parent__": function(node, nodeStart) {
					// allow access if path started from a hidden material sample
					return nodeStart && nodeStart.HiddenProduct;
				}
			},
			Child: {
				"__Parent__": "ProductSourcing"
			}
		},
		"ProductSourcing": {
			Access: {
				"__Parent__": Boolean
			},
			Child: {
				"__Parent__": "Product",
			}
		},
		"ProjectLineItem": {
			Access: {
				"Product": Boolean
			}
		},
		"ProofTask": {
			Child: {
				"__Parent__": "ProofStage"
			}
		},
		"PurchasedOrder": {
			Access: {
				"ShipmentTerms": Boolean
			},
			Child: {
				"Orders": "Order"
			}
		},
		"PurchasedOrderColor": {
			Access: {
				"PO": Boolean,
				"__Parent__": Boolean,
				"Orders": Boolean
			}
		},
		"PurchasedOrderProduct": {
			Access: {
				"PO": Boolean,
				"__Parent__": Boolean,
				"Orders": Boolean
			},
			Child: {
				"Orders": "BaseOrder"
			}
		},
		"RealizedProduct": {
			Access: {
				"SKUProductSources": Boolean
			},
			Child: {
				"__Parent__": "Product"
			}
		},
		"RecipeDataSheet": {
			Child: {
				"__Parent__": "FormulatedProduct"
			}
		},
		"ReviewFood": {
			Child: {
				"__Parent__": "FormulatedProduct"
			}
		},
		"Routing": {
			Child: {
				"__Parent__": "BaseProduct"
			}
		},
		"RoutingRevision": {
			Child: {
				"Items": "RoutingItem"
			}
		},
		"RoutingItem": {
			Child: {
				"Actual": "RoutingItemReferenceable",
				"Original": "RoutingItemReferenceable",
			}
		},
		"SalesProductGroup": {
			Access: {
				"GroupProducts": Boolean
			}
		},
		"Sample": {
			Child: {
				"__SampleSizeChart__": "SizeChart",		// to support Sample Dimensions views
			}
		},
		"SampleStorage": {
			Access: {
				"SampleStorageSamples": Boolean
			}
		},
		"SeasonAttributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"SelectSetItem": {
			Access: {
				"Item": Boolean
			}
		},
		"Shape": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"ShipmentTerms": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"SiteLibSupplierItem": {
			Access: {
				"Templates": Boolean
			}
		},
		"SizeChart": {
			Child: {
				"__Parent__": "Style"
			}
		},
		"SizeChartAdmin": {
			Access: {
				"SpecTemplates": Boolean,
				"StyleBOMTemplates": Boolean,
				"Templates": Boolean
			}
		},
		"SizeChartReview": {
			Child: {
				"__Parent__": "Style"
			},
			Parent: {
				"DataSheets": "Style"
			}
		},
		"SizeChartRevision": {
			Access: {
				"Review": Boolean
			},
			Child: {
				"Items": "SizeChartDimension"
			}
		},
		"SKUAttributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"SKUMaterialAttributes": {
			Access: {
				"__Parent__": Boolean
			}
		},
		"SOLineItem": {
			Access: {
				"__Parent__": Boolean,
				"ConsolidatedBOMLines": Boolean
			}
		},
		"SpecificationDataSheet": {
			Child: {
				"__Parent__": "BaseProduct"
			}
		},
		"SRLineItem": {
			Access: {
				"SRLineItemSamples": Boolean
			}
		},
		"Style": {
			Access: {
				"CompetitiveStyles": Boolean
			},
			Child: {
				"__Parent__": "Collection",
			}
		},
		"StyleAttributes": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"StyleCopyOptions": {
			Child: {
				"RootSubType": "StyleType"
			}
		},
		"StyleSourcing": {
			Access: {
				"__Parent__": Boolean,
				"ProductionQuote": Boolean,
				"SampleQuote": Boolean
			},
		},
		"StyleSpecification": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"SubRoutingRevision": {
			Child: {
				"Items": "SubRoutingItem"
			}
		},
		"SubRoutingItem": {
			Child: {
				"Actual": "RoutingItemReferenceable",
				"Original": "RoutingItemReferenceable",
			}
		},
		"Supplier": {
			Access: {
				"Factories": Boolean,
				"Suppliers": Boolean
			},
		},
		"SupplierFactory": {
			Access: {
				"__Parent__": Boolean,
				"Factory": Boolean
			}
		},
		"SupplierItem": {
			Access: {
				"__Parent__": Boolean
			},
			Child: {
				"__Parent__": "ProductSource"
			}
		},
		"SupplierRequest": {
			Access: {
				"SRSources": Boolean
			}
		},
		"Test": {
			Child: {
				"__Parent__": "TestRunSection"
			}
		},
		"TestRun": {
			Child: {
				"__Parent__": "BaseProduct"
			}
		},
		"TestSpecGroup": {
			Access: {
				"TestSpecs": Boolean
			}
		},
		"Theme": {
			Access: {
				"__Parent__": Boolean
			},
		},
		"VariantComponent": {
			Access: {
				"Component": Boolean
			}
		},
		"Wbs": {
			Child: {
				// Hard code WbsHolder to Style
				"__Parent__": "Style"
			}
		}
	};

	/**
	 * @property {Object} _3DMaterialOwners - map of domain object types that support 3D materials.
	 * This is a subset of those that inherit from DocAndCommentOwner.
	 */
	const _3DMaterialOwners = {
		ColorMaterial: 1,
		Material: 1,
		MaterialSample: 1,
		Sample: 1,
		SupplierItem: 1,
		SRLineItem: 1
	};

	/**
	 * @property {Object} _referenceDocumentOwners - map of domain object types that support reference documents.
	 * This is a subset of those that inherit from DocAndCommentOwner.
	 */
	const _referenceDocumentOwners = {
		Category1: 1,
		Category2: 1,
		Collection: 1,
		ColorMaterial: 1,
		ColorSpecification: 1,
		CompetitiveFood: 1,
		CompetitiveStyle: 1,
		CustomerPurchaseOrder: 1,
		EngineeringChange: 1,
		Factory: 1,
		Food: 1,
		FoodSample: 1,
		FoodSKU: 1,
		FoodSource: 1,
		FoodSupplierItem: 1,
		MarketingCollection: 1,
		Material: 1,
		MaterialSample: 1,
		PrintDesign: 1,
		PrintDesignColor: 1,
		PrintSpecification: 1,
		ProductHierarchy: 1,
		ProductHierarchyFolder: 1,
		ProductSource: 1,
		Project: 1,
		PurchasedOrder: 1,
		FormulatedMaterial: 1,
		FormulatedMaterialSample: 1,
		Sample: 1,
		Season: 1,
		Shape: 1,
		ShipmentTerms: 1,
		SKU: 1,
		SKUMaterial: 1,
		SRLineItem: 1,
		Style: 1,
		Supplier: 1,
		SupplierItem: 1,
		Test: 1,
		TestRunRevision: 1,
		TestSpecRevision: 1,
		Theme: 1
	};

	function _key(type, key, param) {
		return type + "." + key + (param ? ("." + param) : "");
	}

	function _addHint(key, value) {
		var v = lang.getObject(key, false, _hints);

		if (v && (v != value)) {
			console.warn("reflectionHints: multiple mappings for " + key + ": (" + v + "," + value + ")");
		}

		lang.setObject(key, value, _hints);
	}

	function _getHint(key) {
		return lang.getObject(key, false, _hints);
	}

	function getAllHints() {
		return _hints;
	}

	/**
	 * Add function for nodes of given type to determine if they can be navigated to
	 * @param {string} type
	 * @param {(node: C8Node) => boolean} f
	 */
	function addLinkValidation(type, f) {
		_addHint(_key(type, LINK), f);
	}

	/**
	 * Configure availability of 3D material on given type
	 * @param {string} type - domain object type
	 * @param {boolean} allow
	 */
	function allow3DMaterialOnType(type, allow) {
		if (allow) {
			_3DMaterialOwners[type] = 1;
		} else {
			delete _3DMaterialOwners[type];
		}
	}

	/**
	 * Configure availability of reference documents on given type
	 * @param {string} type - domain object type
	 * @param {boolean} allow
	 */
	function allowReferenceDocumentsOnType(type, allow) {
		if (allow) {
			_referenceDocumentOwners[type] = 1;
		} else {
			delete _referenceDocumentOwners[type];
		}
	}

	/**
	 * Return key to use in local storage to track selected tab based on location node
	 */
	function getLocationHistoryKey(node, revisionNode) {
		const type = node.$Type;
		const callback = _locationHistoryCallbacks[type];
		return (callback ? callback(node, revisionNode) : null) || type;
	}

	/**
	 * Set function to call to determine key to use for tracking selected tab based on location node
	 */
	function setLocationHistoryKeyCallback(type, callback) {
		_locationHistoryCallbacks[type] = callback;
	}

	/**
	 * Call type-specific function for customizing layout if set
	 */
	async function customizeLocationLayout(defView, node, revisionNode) {
		const callback = _locationLayoutCallbacks[node.$Type];
		if (callback) {
			await callback(defView, node, revisionNode);
		}
	}

	/**
	 * Set function to call to customize the layout view for nodes of the given type
	 * @param {string} type
	 * @param {LocationLayoutCallback} callback
	 */
	function setLocationLayoutCallback(type, callback) {
		_locationLayoutCallbacks[type] = callback;
	}

	/**
	 * Return any attributes that should be hidden within the domain class
	 * @param {string} domain
	 * @return {string[]|null}
	 */
	function getPrivateSecondaryAttributes(domain) {
		return PRIVATE_SECONDARY_ATTRIBUTES[domain];
	}

	/**
	 * Return true if 3D materials are allowed for this domain object
	 * @param {string} type - domain object type
	 * @return {boolean}
	 */
	function is3DMaterialOwner(type) {
		return !!_3DMaterialOwners[type];
	}

	/**
	 * Return true if node containing image (that is not derived from ImageContainer) allows editing image attributes
	 * @param {C8NodeType} type
	 * @return {boolean}
	 */
	function doesImageContainerAllowAttributes(type) {
		return IMAGE_CONTAINERS_ALLOW_ATTRIBUTES.includes(type);
	}

	/**
	 * Return true if node containing image (that is not derived from ImageContainer) allows editing the image file
	 * @param {C8NodeType} type
	 * @return {boolean}
	 */
	function doesImageContainerAllowEdit(type) {
		return IMAGE_CONTAINERS_ALLOW_EDIT.includes(type);
	}

	/**
	 * Return true if referenced documents are allowed for this domain object
	 * @param {string} type - domain object type
	 * @return {boolean}
	 */
	function isReferenceDocumentOwner(type) {
		return !!_referenceDocumentOwners[type];
	}

	/**
	 * Return list of domain objects that can contain 3D material
	 * @return {string[]}
	 */
	function get3DMaterialOwners() {
		return _.keys(_3DMaterialOwners);
	}

	/**
	 * Return list of domain objects that can contain referenced documents
	 * @return {string[]}
	 */
	function getReferenceDocumentOwners() {
		return _.keys(_referenceDocumentOwners);
	}

	return {
		addCommentPath,
		addLinkValidation,
		addQueryConditionsForTemplate,
		allow3DMaterialOnType,
		allowReferenceDocumentsOnType,
		customizeLocationLayout,
		doesImageContainerAllowAttributes,
		doesImageContainerAllowEdit,
		getAllHints,
		get3DMaterialOwners,
		getCommentPaths,
		getLocationHistoryKey,
		getPrivateSecondaryAttributes,
		getReferenceDocumentOwners,
		hasCommentPaths,
		is3DMaterialOwner,
		isReferenceDocumentOwner,
		isRevisableNodeWithState,
		isRevisableNodeWithApprovedLatest,
		isTemplateNode,
		isTemplateType,
		removeCommentPath,
		setLocationHistoryKeyCallback,
		setLocationLayoutCallback,

		/** Return true if the given type is the root type of a landing page navigated to from the page header */
		isTypeOfLandingPage(type) {
			return [TYPE_DATA_SETUP, TYPE_HOME, TYPE_SITE, TYPE_TASKS,].includes(type);
		},

		getAccess: function(/*String*/type, /*String*/idRelation) {
			return _getHint(_key(type, ACCESS, idRelation));
		},

		canLinkToNode: function(/*Node*/node) {
			if (node) {
				const f = _getHint(_key(node.$Type, LINK));
				return !f || f(node);
			}

			return false;
		},

		getLocationNodeStatus: function(/*Node*/node, /*Boolean?*/bCurNode) {
			// this is to override inactive status by frozen if node is frozen
			if (bCurNode && xml.nodeIsFrozen(node)) {
				return _.contains(FREEZE_EXCLUSION_LIST, node.$Type) ? "" : "StateFrozen";
			} else if (node && node.Active === false) {
				return "StateInactive";
			} else {
				return "";
			}
		},

		getParentReplacement: function(/*String*/type) {
			return TYPES_WITH_DUPLICATE_PARENT[type];
		},

		getSortAttribute: function(/*String*/type) {
			return _getHint(_key(type, SORT));
		},

		getTarget: function(/*String*/type, /*String*/idRelation, /*Boolean*/bParent) {
			return _getHint(_key(type, bParent ? xml.PATH_OP_PARENT : xml.PATH_OP_CHILD, idRelation));
		},

		getWatchFunction: function(/*String*/type, /*String*/idAttribute) {
			return _getHint(_key(type, WATCH, idAttribute));
		},

		isSpecialPath: function(/*String*/type, /*String*/path) {
			return !!path && !!specialPaths[type]?.includes(path)
		},

		setAccess: function(/*String*/type, /*String*/idRelation, /*Function?*/f) {
			_addHint(_key(type, ACCESS, idRelation), f);
		},

		setSortAttribute: function(/*String*/type, /*String*/idAttr) {
			_addHint(_key(type, SORT), idAttr);
		},

		setSpecialPath: function(/*String*/type, /*String*/path) {
			csi.objectArrayAddValue(specialPaths, type, path);
		},

		setTarget: function(/*String*/type, /*String*/idRelation, /*String*/targetType, /*Boolean*/bParent) {
			_addHint(_key(type, bParent ? xml.PATH_OP_PARENT : xml.PATH_OP_CHILD, idRelation), targetType);
		},

		watchAttribute: function(/*String*/type, /*String*/idAttribute, /*Function*/f) {
			_addHint(_key(type, WATCH, idAttribute), f);
		}
	};
});
